import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Converts minutes from start of day to a time string in HH:MM format
 * @param minutes Minutes from start of day (e.g., 540 for 09:00)
 * @returns Time string in HH:MM format
 */
export function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

/**
 * Converts a time string in HH:MM format to minutes from start of day
 * @param timeString Time string in HH:MM format
 * @returns Minutes from start of day
 */
export function timeStringToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * @deprecated Legacy shift-template time model: templates created without a
 * `timezone` store minutes-from-UTC-midnight, converted with the browser offset
 * at creation time (not DST-stable). New templates store raw local minutes plus
 * `browserTimezone()`. Kept only to interpret legacy data.
 */
export function minutesOffsetInUTC(minutes: number): number {
  return minutes - dayjs().utcOffset()
}

/** The browser's IANA timezone (e.g. 'Europe/Paris'). */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Minutes-from-midnight of a shift template, converted to the viewer's local
 * wall clock (for the edit form / template labels).
 *
 * Templates with a `timezone` store wall-clock minutes in that zone; without one
 * (legacy) they store minutes-from-UTC-midnight. Both are mapped onto a reference
 * calendar day (the template's start date, else today) so DST is respected.
 */
export function templateMinutesToLocal(minutes: number, tz: string | undefined, referenceDate?: string | Date): number {
  const day = dayjs(referenceDate ?? new Date()).format('YYYY-MM-DD')
  const hours = Math.floor(minutes / 60) % 24
  const wallClock = `${day} ${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
  const instant = tz ? dayjs.tz(wallClock, tz) : dayjs.utc(wallClock)
  return instant.local().hour() * 60 + instant.local().minute()
}
